import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { LuCheck, LuPlus, LuRotateCcw, LuShoppingCart, LuInfo } from 'react-icons/lu';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchProductDetails, addToCart } from '../../services/apiService';
import { trackEvent } from '../../services/analytics/analyticsService';
import { getArticleImageUrl } from '../../utils/imageUtils';
import PriceDisplay from '../common/PriceDisplay';
import { scrollToTop } from '../../utils/scrollUtils';
import { GUTTER, PANEL_GAP, DotGrid, Eyebrow, SectionTitle, PillButton } from './landingUi';
import { CABINET_ITEMS, tierFor, useCabinetState } from './cabinetState';

const EASE = [0.22, 1, 0.36, 1];
const TOTAL = CABINET_ITEMS.length;
const SHELVES = [CABINET_ITEMS.slice(0, 3), CABINET_ITEMS.slice(3, 6), CABINET_ITEMS.slice(6, 9)];

/* ───────────────────────── Packs on the shelf ─────────────────────────
   Each essential is drawn as a small pack in CSS — a box, bottle, spray,
   tube or thermometer — so the cabinet reads as a cabinet at a glance and
   stays crisp at any size. A missing item is the same silhouette, dashed. */

function Pack({ item, have }) {
  const { shape, tint, Icon } = item;
  const body = have
    ? 'bg-white ring-1 ring-black/[0.08] shadow-[0_6px_12px_-8px_rgba(17,24,39,0.5)]'
    : 'border-2 border-dashed border-gray-300/90 bg-white/40';
  const iconColor = have ? tint : '#C9C2BA';
  const accent = have ? tint : 'transparent';

  if (shape === 'stick') {
    // Thermometer lying on the shelf, with a little display.
    return (
      <div className={`relative flex h-[22%] w-[82%] items-center rounded-full ${body}`}>
        <span className="ml-[6%] h-[55%] w-[16%] rounded-full" style={{ background: accent, opacity: have ? 0.9 : 0 }} />
        <span className={`ml-auto mr-[10%] rounded-[3px] px-1 font-mono text-[8px] font-bold leading-tight ${have ? 'bg-[#E8F1F8] text-[#3B82C4]' : 'text-gray-300'}`}>
          37.2°
        </span>
      </div>
    );
  }
  if (shape === 'tube') {
    // Ointment tube lying down, cap to the right.
    return (
      <div className="flex h-[36%] w-[84%] items-center">
        <div className={`relative flex h-full flex-1 items-center justify-center rounded-l-[3px] rounded-r-[14px] ${body}`}>
          <span className="absolute inset-y-[10%] left-0 w-[8%] rounded-l-[2px]" style={{ background: accent, opacity: 0.25 }} />
          <Icon className="h-[46%] w-auto" style={{ color: iconColor }} aria-hidden="true" />
        </div>
        <span className="h-[52%] w-[14%] rounded-r-md" style={{ background: have ? tint : 'transparent', border: have ? 'none' : '2px dashed #D6D0C9' }} />
      </div>
    );
  }
  if (shape === 'spray' || shape === 'bottle') {
    const isSpray = shape === 'spray';
    return (
      <div className="flex h-[88%] w-full flex-col items-center justify-end">
        {isSpray && (
          <span className="h-[9%] w-[10%] rounded-t-sm" style={{ background: have ? '#9CA3AF' : 'transparent', border: have ? 'none' : '2px dashed #D6D0C9' }} />
        )}
        <span
          className={`${isSpray ? 'h-[11%] w-[26%]' : 'h-[14%] w-[30%]'} rounded-t-md`}
          style={{ background: have ? tint : 'transparent', border: have ? 'none' : '2px dashed #D6D0C9' }}
        />
        <div className={`relative flex ${isSpray ? 'h-[66%] w-[38%]' : 'h-[64%] w-[46%]'} flex-col items-center justify-center overflow-hidden rounded-[12px] ${body}`}>
          <span className="absolute inset-x-0 top-[28%] h-[44%]" style={{ background: accent, opacity: 0.12 }} />
          <Icon className="relative h-[34%] w-auto" style={{ color: iconColor }} aria-hidden="true" />
        </div>
      </div>
    );
  }
  // Box (tablets, plasters, lozenges).
  return (
    <div className={`relative flex h-[76%] w-[60%] flex-col overflow-hidden rounded-[10px] ${body}`}>
      <span className="h-[24%] w-full" style={{ background: accent }} />
      <div className="flex flex-1 items-center justify-center">
        <Icon className="h-[46%] w-auto" style={{ color: iconColor }} aria-hidden="true" />
      </div>
      {have && <span className="absolute bottom-[12%] left-[16%] right-[16%] h-[6%] rounded-full bg-black/[0.06]" />}
    </div>
  );
}

function Slot({ item, have, label, stateLabel, onToggle, reduced }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(item.id)}
      aria-pressed={have}
      aria-label={`${label}: ${stateLabel}`}
      className="group/slot relative flex flex-col items-center focus:outline-none"
    >
      {/* Pack area; the shelf board sits right under it (see Shelf). */}
      <div className="relative flex h-[var(--slot-h)] w-full items-end justify-center rounded-xl transition-colors group-hover/slot:bg-white/50 group-focus-visible/slot:ring-2 group-focus-visible/slot:ring-brand/40">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={have ? 'have' : 'missing'}
            className="flex h-full w-full items-end justify-center pb-0.5"
            initial={reduced ? false : have ? { y: -34, opacity: 0, rotate: -8 } : { opacity: 0, scale: 0.9 }}
            animate={{ y: 0, opacity: 1, rotate: 0, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { y: -10, opacity: 0, scale: 0.92, transition: { duration: 0.15 } }}
            transition={have ? { type: 'spring', stiffness: 420, damping: 17 } : { duration: 0.2 }}
          >
            <Pack item={item} have={have} />
          </motion.div>
        </AnimatePresence>

        {/* Corner badge: a green tick when it's there, a plus when it's not. */}
        <AnimatePresence initial={false}>
          {have ? (
            <motion.span
              key="tick"
              className="absolute right-[6%] top-[4%] inline-flex h-5 w-5 lg:h-6 lg:w-6 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_4px_10px_-4px_rgba(16,185,129,0.9)]"
              initial={reduced ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 20, delay: reduced ? 0 : 0.12 }}
              aria-hidden="true"
            >
              <LuCheck className="h-3 w-3" strokeWidth={3} />
            </motion.span>
          ) : (
            <motion.span
              key="plus"
              className="absolute right-[6%] top-[4%] inline-flex h-5 w-5 lg:h-6 lg:w-6 items-center justify-center rounded-full bg-white text-gray-400 ring-1 ring-black/[0.08] transition-colors group-hover/slot:bg-brand group-hover/slot:text-white"
              initial={reduced ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              aria-hidden="true"
            >
              <LuPlus className="h-3 w-3" strokeWidth={3} />
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Shelf tag hanging under the board, like a price label. */}
      <span
        className={`mt-[0.85rem] inline-flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold leading-tight ring-1 transition-colors sm:text-[11px] xl:px-2 xl:text-[12.5px] ${
          have ? 'bg-white text-gray-800 ring-emerald-500/30' : 'bg-white/70 text-gray-500 ring-black/[0.06]'
        }`}
      >
        <span className={`hidden h-1.5 w-1.5 flex-shrink-0 rounded-full min-[400px]:block ${have ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-hidden="true" />
        {/* Two lines on narrow phones rather than a cut-off name; long single
            words carry soft hyphens in the translations for the same reason. */}
        <span className="line-clamp-2 text-left">{label}</span>
      </span>
    </button>
  );
}

/* ───────────────────────────── The cabinet ─────────────────────────────
   A white-lacquered wall cabinet: a crown with the pharmacy sign on top, a
   plinth below, hinges on the sides, a recessed interior with shaded side
   walls and a planked back, and three wall-to-wall shelves.

   Two glass doors swing open on their hinges the first time it scrolls into
   view and stay open beside it — an open door is the clearest "this is a
   cabinet" cue there is. With reduced motion they are simply shown open. */

const CROSS_PATH =
  'M9.5 3h5a1 1 0 0 1 1 1v4.5H20a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-4.5V20a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-4.5H4a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1h4.5V4a1 1 0 0 1 1-1z';
// White lacquer: a touch of warmth so it isn't flat screen-white.
const LACQUER = 'linear-gradient(180deg, #FFFFFF 0%, #FBF9F7 55%, #F3EFEB 100%)';
// Past 90° so an open door clears the opening and sits beside the cabinet,
// showing its inside face like a real one.
const DOOR_OPEN_DEG = 104;

function Hinge({ side, pos }) {
  return (
    <span
      aria-hidden="true"
      className={`absolute ${pos} z-20 h-7 w-[5px] rounded-full lg:h-9 lg:w-[6px] ${side === 'left' ? '-left-[3px]' : '-right-[3px]'}`}
      style={{ background: 'linear-gradient(90deg, #B9B3AD, #E7E2DD 45%, #A9A29B)' }}
    />
  );
}

function Cabinet({ have, onToggle, label, stateLabel, reduced, inView }) {
  const doorsOpen = reduced || inView;

  return (
    // --slot-h sizes every shelf (slot height and board position together):
    // compact on phones, growing with the viewport on large screens. The side
    // padding is the room the open doors swing into.
    <div
      className="relative mx-auto w-full max-w-[28rem] px-[12%] pt-8 lg:px-[11%] [--slot-h:clamp(4.25rem,9vw,5.25rem)] lg:max-w-[38rem] lg:pt-10 lg:[--slot-h:clamp(4.75rem,6.8vw,7.75rem)] 2xl:max-w-[42rem]"
    >
      <div className="relative">
        {/* Crown, slightly wider than the body, carrying the pharmacy sign */}
        <div
          aria-hidden="true"
          className="relative -mx-2 h-4 rounded-t-[12px] rounded-b-[3px] ring-1 ring-black/[0.06] lg:-mx-3 lg:h-5"
          style={{ background: LACQUER, boxShadow: '0 6px 10px -6px rgba(17,24,39,0.3)' }}
        >
          <span className="absolute bottom-full left-1/2 mb-[-6px] flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-xl bg-brand shadow-[0_12px_24px_-10px_rgb(var(--brand)/0.8)] ring-4 ring-white lg:h-14 lg:w-14 lg:rounded-2xl">
            <svg viewBox="0 0 24 24" className="h-5 w-5 text-white lg:h-7 lg:w-7" fill="currentColor"><path d={CROSS_PATH} /></svg>
          </span>
        </div>

        {/* Carcass */}
        <div
          className="relative rounded-[6px] p-2.5 ring-1 ring-black/[0.06] sm:p-3 lg:p-4"
          style={{ background: LACQUER }}
        >
          <Hinge side="left" pos="top-[14%]" />
          <Hinge side="left" pos="bottom-[14%]" />
          <Hinge side="right" pos="top-[14%]" />
          <Hinge side="right" pos="bottom-[14%]" />

          {/* Interior: recessed, shaded side walls, planked back */}
          <div
            className="relative overflow-hidden rounded-[4px] px-2 pb-3 pt-5 sm:px-3 lg:px-5 lg:pb-5 lg:pt-8"
            style={{
              background:
                'repeating-linear-gradient(90deg, transparent 0 calc(25% - 1px), rgba(120,96,72,0.07) calc(25% - 1px) 25%), linear-gradient(180deg, #ECE5DE 0%, #F4EFEA 45%, #EEE7E0 100%)',
              boxShadow:
                'inset 16px 0 18px -14px rgba(17,24,39,0.28), inset -16px 0 18px -14px rgba(17,24,39,0.28), inset 0 16px 22px -14px rgba(17,24,39,0.32), inset 0 -8px 14px -10px rgba(17,24,39,0.18)',
            }}
          >
            <div className="relative space-y-3 sm:space-y-4 lg:space-y-6">
              {SHELVES.map((shelf, s) => (
                <div key={s} className="relative">
                  {/* Board, wall to wall: light top face, darker front edge. */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -left-2 -right-2 top-[var(--slot-h)] h-3 sm:-left-3 sm:-right-3 lg:-left-5 lg:-right-5 lg:h-3.5"
                    style={{
                      background: 'linear-gradient(180deg, #FBF8F5 0%, #F1EBE5 38%, #DED4C9 40%, #D3C8BC 100%)',
                      boxShadow: '0 10px 14px -10px rgba(17,24,39,0.4)',
                    }}
                  />
                  <div className="relative grid grid-cols-3 gap-1.5 sm:gap-2 lg:gap-4">
                    {shelf.map((item) => (
                      <Slot
                        key={item.id}
                        item={item}
                        have={have.includes(item.id)}
                        label={label(item.id)}
                        stateLabel={stateLabel(have.includes(item.id))}
                        onToggle={onToggle}
                        reduced={reduced}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Doors: lacquered frame around a frosted pane, hinged on the
              outer edges. They close over the opening and swing out. */}
          <div className="pointer-events-none absolute inset-0 z-10 grid grid-cols-2" style={{ perspective: '2600px' }}>
            {['left', 'right'].map((side) => (
              <motion.div
                key={side}
                className={`relative h-full p-2 sm:p-2.5 lg:p-3 ${side === 'left' ? 'rounded-l-[6px]' : 'rounded-r-[6px]'}`}
                style={{
                  transformOrigin: side === 'left' ? 'left center' : 'right center',
                  transformStyle: 'preserve-3d',
                  background: LACQUER,
                  boxShadow: 'inset 0 0 0 1px rgba(17,24,39,0.07), 0 18px 30px -18px rgba(17,24,39,0.45)',
                }}
                initial={reduced ? { rotateY: side === 'left' ? -DOOR_OPEN_DEG : DOOR_OPEN_DEG } : { rotateY: 0 }}
                animate={{ rotateY: doorsOpen ? (side === 'left' ? -DOOR_OPEN_DEG : DOOR_OPEN_DEG) : 0 }}
                // A slow, weighted swing that eases in and out like a real
                // door, the right one a beat behind.
                transition={{ duration: reduced ? 0 : 2.2, ease: [0.65, 0, 0.35, 1], delay: reduced ? 0 : side === 'left' ? 0.2 : 0.4 }}
              >
                {/* Glass pane */}
                <div
                  className="relative h-full overflow-hidden rounded-[3px]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(232,240,244,0.85) 0%, rgba(255,255,255,0.45) 45%, rgba(225,235,240,0.75) 100%)',
                    backdropFilter: 'blur(3px)',
                    WebkitBackdropFilter: 'blur(3px)',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.9), inset 0 2px 6px rgba(17,24,39,0.08)',
                  }}
                >
                  <span className="absolute -left-1/4 top-0 h-full w-1/3 rotate-[18deg] bg-white/60 blur-md" />
                  <span className="absolute left-[55%] top-0 h-full w-[8%] rotate-[18deg] bg-white/40 blur-sm" />
                </div>
                {/* Handle on the closing edge — outside face only, so it
                    disappears once the door has swung past 90°. */}
                <span
                  style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                  className={`absolute top-1/2 h-14 w-1.5 -translate-y-1/2 rounded-full bg-brand shadow-[0_4px_10px_-4px_rgb(var(--brand)/0.9)] lg:h-20 lg:w-2 ${side === 'left' ? 'right-1 sm:right-1.5' : 'left-1 sm:left-1.5'}`}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Plinth */}
        <div
          aria-hidden="true"
          className="relative -mx-1 h-3 rounded-b-[10px] rounded-t-[2px] ring-1 ring-black/[0.06] lg:-mx-1.5 lg:h-4"
          style={{ background: 'linear-gradient(180deg, #F3EFEB 0%, #E6DFD8 100%)' }}
        />
      </div>

      {/* Shadow on the wall below */}
      <div aria-hidden="true" className="mx-auto mt-3 h-5 w-[70%] rounded-[100%] bg-black/[0.09] blur-lg" />
    </div>
  );
}

/* ─────────────────────────── Progress ring ─────────────────────────── */

const BURST = Array.from({ length: 10 }, (_, i) => (i / 10) * Math.PI * 2);

function ProgressRing({ n, reduced }) {
  const r = 30;
  const c = 2 * Math.PI * r;
  const full = n >= TOTAL;
  const [burstKey, setBurstKey] = useState(0);
  const prev = useRef(n);

  // Celebrate once each time the cabinet becomes complete.
  useEffect(() => {
    if (n >= TOTAL && prev.current < TOTAL) setBurstKey((k) => k + 1);
    prev.current = n;
  }, [n]);

  return (
    <div className="relative h-20 w-20 flex-shrink-0">
      <svg viewBox="0 0 72 72" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="36" cy="36" r={r} fill="none" stroke="#EDE6DF" strokeWidth="7" />
        <motion.circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          style={{ stroke: full ? '#10B981' : 'rgb(var(--brand))' }}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={c}
          initial={false}
          animate={{ strokeDashoffset: c * (1 - n / TOTAL) }}
          transition={{ duration: reduced ? 0 : 0.6, ease: EASE }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-[22px] font-extrabold tracking-[-0.04em] text-gray-900 tabular-nums">{n}</span>
        <span className="mt-0.5 text-[10px] font-semibold text-gray-400">/ {TOTAL}</span>
      </div>

      {!reduced && (
        <AnimatePresence>
          {burstKey > 0 && (
            <div key={burstKey} className="pointer-events-none absolute inset-0" aria-hidden="true">
              {BURST.map((angle, i) => (
                <motion.svg
                  key={i}
                  viewBox="0 0 24 24"
                  className="absolute left-1/2 top-1/2 h-3 w-3 text-brand"
                  fill="currentColor"
                  initial={{ x: '-50%', y: '-50%', scale: 0, opacity: 1 }}
                  animate={{ x: `calc(-50% + ${Math.cos(angle) * 58}px)`, y: `calc(-50% + ${Math.sin(angle) * 58}px)`, scale: [0, 1.2, 0.9], opacity: [1, 1, 0], rotate: 90 }}
                  transition={{ duration: 1.1, ease: EASE }}
                >
                  <path d="M9.5 3h5a1 1 0 0 1 1 1v4.5H20a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-4.5V20a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-4.5H4a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1h4.5V4a1 1 0 0 1 1-1z" />
                </motion.svg>
              ))}
            </div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}

/* ───────────────────────────── The section ───────────────────────────── */

/**
 * "Is your home medicine cabinet complete?" — the visitor taps what they
 * already have; what's left becomes a short list of suggested products with
 * one button to add them all. Ticks persist on the device (no account), see
 * medicineCabinet.js. Products load only once the section is near the
 * viewport.
 */
const MedicineCabinet = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const sectionRef = useRef(null);
  const cabinetRef = useRef(null);
  const nearView = useInView(sectionRef, { once: true, margin: '300px 0px' });
  const cabinetInView = useInView(cabinetRef, { once: true, amount: 0.45 });
  const { have, toggle, reset } = useCabinetState();

  const [articles, setArticles] = useState({});
  // pharmacode → 'adding' | 'added' | 'error'
  const [cartState, setCartState] = useState({});
  const [addingAll, setAddingAll] = useState(false);

  const c = (key) => t(`home.cabinet.${key}`);
  const label = (id) => c(`items.${id}`);
  const stateLabel = (isHave) => (isHave ? c('have') : c('missing'));

  useEffect(() => {
    if (!nearView) return undefined;
    let cancelled = false;
    Promise.all(
      CABINET_ITEMS.map(async ({ pharmacode }) => {
        try {
          const res = await fetchProductDetails(pharmacode);
          const article = res?.data?.product?.articles?.find((a) => String(a.pharmacode) === pharmacode);
          return [pharmacode, article || null];
        } catch {
          return [pharmacode, null];
        }
      })
    ).then((entries) => {
      if (!cancelled) setArticles(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [nearView]);

  const n = have.length;
  const tier = tierFor(n, TOTAL);
  const missing = CABINET_ITEMS.filter((i) => !have.includes(i.id));
  const buyable = missing.filter((i) => articles[i.pharmacode] && cartState[i.pharmacode] !== 'added');

  const addOne = async (pharmacode) => {
    setCartState((s) => ({ ...s, [pharmacode]: 'adding' }));
    try {
      const res = await addToCart(pharmacode, 1);
      if (res?.success === false) throw new Error('failed');
      setCartState((s) => ({ ...s, [pharmacode]: 'added' }));
      return true;
    } catch {
      setCartState((s) => ({ ...s, [pharmacode]: 'error' }));
      return false;
    }
  };

  const addAllMissing = async () => {
    if (!buyable.length) return;
    setAddingAll(true);
    trackEvent('cabinet_add_missing', { count: buyable.length });
    // One at a time: the guest cart is read-modify-written per request.
    for (const item of buyable) {
      // eslint-disable-next-line no-await-in-loop
      await addOne(item.pharmacode);
    }
    setAddingAll(false);
  };

  const openProduct = (pharmacode) => {
    navigate(`/product/${pharmacode}`);
    setTimeout(() => scrollToTop(), 100);
  };

  const anyError = missing.some((i) => cartState[i.pharmacode] === 'error');

  return (
    <section ref={sectionRef} className={`${GUTTER} ${PANEL_GAP}`} aria-label={c('eyebrow')}>
      <div className="relative overflow-hidden rounded-[2.5rem] bg-[#F6F3F0] px-4 py-10 ring-1 ring-black/[0.04] sm:px-8 lg:px-[clamp(2rem,4.5vw,5rem)] lg:py-[clamp(2.75rem,5vw,5rem)]">
        <DotGrid maskImage="radial-gradient(80% 70% at 70% 40%, #000 30%, transparent 80%)" />
        {/* Warm glow behind the cabinet */}
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-1/2 h-[30rem] w-[30rem] -translate-y-1/2 rounded-full bg-brand/[0.07] blur-3xl" />

        <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Heading — first on phones */}
          <div className="lg:col-span-6 lg:col-start-1 lg:row-start-1">
            <Eyebrow pulse>{c('eyebrow')}</Eyebrow>
            <SectionTitle lead={c('titleLead')} highlight={c('titleHighlight')} className="mt-4 !text-[clamp(1.9rem,3.2vw,3.25rem)]" />
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-gray-600">{c('intro')}</p>
          </div>

          {/* Cabinet */}
          <div ref={cabinetRef} className="lg:col-span-6 lg:col-start-7 lg:row-span-2 lg:row-start-1" role="group" aria-label={`${c('titleLead')} ${c('titleHighlight')}`}>
            <Cabinet have={have} onToggle={toggle} label={label} stateLabel={stateLabel} reduced={reduced} inView={cabinetInView} />
            <p className="mt-2 text-center text-[12px] font-medium text-gray-400">{c('tapHint')}</p>
          </div>

          {/* Progress + missing */}
          <div className="lg:col-span-6 lg:col-start-1 lg:row-start-2 lg:self-start">
            <div className="rounded-[1.75rem] bg-white p-4 ring-1 ring-black/[0.05] shadow-[0_24px_48px_-36px_rgba(17,24,39,0.5)] sm:p-5">
              <div className="flex items-center gap-4">
                <ProgressRing n={n} reduced={reduced} />
                <div className="min-w-0 flex-1" aria-live="polite">
                  <p className="text-[18px] font-extrabold tracking-[-0.03em] text-gray-900">{c(`tiers.${tier}`)}</p>
                  <p className="mt-0.5 text-sm text-gray-500">
                    {tier === 'full' ? c('fullBody') : c('progress').replace('{n}', n).replace('{total}', TOTAL)}
                  </p>
                </div>
                {n > 0 && (
                  <button
                    type="button"
                    onClick={reset}
                    className="hidden flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-gray-400 transition-colors hover:bg-[#F6F3F0] hover:text-gray-700 sm:inline-flex"
                  >
                    <LuRotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                    {c('reset')}
                  </button>
                )}
              </div>

              {missing.length > 0 && (
                <>
                  <h3 className="mt-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">{c('missingTitle')}</h3>
                  <ul className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <AnimatePresence initial={false}>
                      {missing.map((item) => {
                        const article = articles[item.pharmacode];
                        const state = cartState[item.pharmacode];
                        const img = article ? getArticleImageUrl(article) : null;
                        return (
                          <motion.li
                            key={item.id}
                            layout={!reduced}
                            initial={reduced ? false : { opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9, transition: { duration: 0.18 } }}
                            className="flex items-center gap-3 rounded-2xl bg-[#FAF8F6] p-2 ring-1 ring-black/[0.04]"
                          >
                            <button
                              type="button"
                              onClick={() => article && openProduct(item.pharmacode)}
                              className="flex min-w-0 flex-1 items-center gap-3 text-left"
                              disabled={!article}
                            >
                              <span className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-black/[0.05]">
                                {img ? (
                                  <img src={img} alt="" loading="lazy" className="h-full w-full object-contain p-1 mix-blend-multiply" />
                                ) : (
                                  <item.Icon className="h-5 w-5" style={{ color: item.tint }} aria-hidden="true" />
                                )}
                              </span>
                              <span className="min-w-0">
                                <span className="block text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: item.tint }}>
                                  {label(item.id)}
                                </span>
                                <span className="block truncate text-[13px] font-semibold text-gray-800">
                                  {article?.description?.longDescription || (Object.keys(articles).length ? c('browse') : '…')}
                                </span>
                                {article && (
                                  <span className="block text-[12.5px]">
                                    <PriceDisplay article={article} variant="compact" />
                                  </span>
                                )}
                              </span>
                            </button>

                            {article && (
                              <button
                                type="button"
                                onClick={() => addOne(item.pharmacode)}
                                disabled={state === 'adding' || state === 'added' || addingAll}
                                aria-label={`${c('add')}: ${article.description?.longDescription || label(item.id)}`}
                                className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 disabled:cursor-default ${
                                  state === 'added'
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-white text-brand ring-1 ring-black/[0.08] hover:bg-brand hover:text-white active:scale-95'
                                }`}
                              >
                                {state === 'adding' ? (
                                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true" />
                                ) : state === 'added' ? (
                                  <LuCheck className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
                                ) : (
                                  <LuShoppingCart className="h-4 w-4" aria-hidden="true" />
                                )}
                              </button>
                            )}
                          </motion.li>
                        );
                      })}
                    </AnimatePresence>
                  </ul>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <PillButton
                      onClick={addAllMissing}
                      disabled={addingAll || buyable.length === 0}
                      className="disabled:cursor-default disabled:opacity-60"
                    >
                      {addingAll
                        ? c('adding')
                        : buyable.length === 0
                          ? c('added')
                          : c('addAll').replace('{n}', buyable.length)}
                    </PillButton>
                    {anyError && <span className="text-xs font-medium text-red-600" role="alert">{c('addFailed')}</span>}
                  </div>
                </>
              )}

              <p className="mt-4 flex items-center gap-1.5 text-[11px] text-gray-400">
                <LuInfo className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                {c('disclaimer')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MedicineCabinet;
